import React from 'react';
import {FmlxChip} from 'fmlx-common-ui';

/**
 * Custom hook to format table data and handle visibility based on filters.
 *
 * This hook provides two main functionalities:
 * 1. `messageDataFormat`: Formats data for rendering in the table based on the column accessor.
 * 2. `isRowVisible`: Filters rows based on the given filter values.
 *
 * @param {Object} filterValues - Object containing filter values for each column (e.g.
 * ```
 * { sender: 'John', status: 'Published' }).
 * ```
 *
 * @returns {Object} Hook methods.
 * @returns {function} messageDataFormat - Function to format the data for a row based on the column.
 * @example
 * ```
 * const { messageDataFormat } = useMessageFormat();
 *
 * // Usage inside a component
 * const formattedValue = messageDataFormat(column, row);
 *
 * // Example usage with columns and rows in a table
 * <FmlxTableBody>
 *   {data.map((row) => (
 *     isRowVisible(row) && (
 *       <FmlxTableRow key={row.id}>
 *         {columns.map((column) => (
 *           <FmlxTableCell key={column.accessor}>
 *             {messageDataFormat(column, row)}
 *           </FmlxTableCell>
 *         ))}
 *       </FmlxTableRow>
 *     )
 *   ))}
 * </FmlxTableBody>
 * ```
 */

export const useColumnFormat = (columnAccessor, condition) => {
  const columnFormat = (column, row) => {
    if (column.Cell) {
      return <column.Cell row={row}/>;
    }
    const formattedValue = (() => {
      if (column.accessor === columnAccessor.STATUS) {
        return getStatusChip(row.status);
      }
      return row[column.accessor];
    })();

    return formattedValue;
  };

  const getStatusChip = (status) => {
    switch (status) {
      case condition.NEW:
        return <FmlxChip label={status} variant={'filled'} size="md" type={FmlxChip.Type.DEFAULT}/>;
      case condition.PUBLISHED:
        return <FmlxChip label={status} variant={'filled'} size="md" type={FmlxChip.Type.SAFE}/>;
      case condition.REJECTED:
        return <FmlxChip label={status} variant={'filled'} size="md" type={FmlxChip.Type.DANGER}/>;
      case condition.NOT_YET:
        return <FmlxChip label={status} variant={'filled'} size="md" type={FmlxChip.Type.DEFAULT}/>;
      case condition.DONE:
        return <FmlxChip label={status} variant={'filled'} size="md" type={FmlxChip.Type.SAFE}/>;
      case condition.IN_PROGRESS:
        return <FmlxChip label={status} variant={'filled'} size="md" type={FmlxChip.Type.WARNING}/>;
      default:
        return null;
    }
  };
  return {columnFormat};
};
